import { Button } from '@progress/kendo-react-buttons';
import React, { memo, useMemo, useRef } from 'react';
import { DateTimeFormatter } from '../../../formatters';
import {
    ChevronLeftOutline,
    ChevronRightOutline,
    DoubleChevronLeftOutline,
    DoubleChevronRightOutline,
    PauseCircleOutline,
    PlayCircleOutline,
    StopCircleOutline
} from '../../../icons/icons';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hooks';
import {
    changeSpeedPlayback,
    pausePlayback,
    playbackSeekCancel,
    playbackSeekStart,
    resumePlayback,
    stopPlayback
} from '../../state/reducers/playback';

export function PlaybackControls() {
    const playback = useAppSelector((x) => x.global.playback);
    const dispatch = useAppDispatch();

    if (!playback.active) {
        return <></>;
    }

    const progress = ((playback.currentTime - playback.startTime) / playback.duration) * 100;

    const handleSeekRequested = async (e: { valuePercentage: number }) => {
        const toTime = Math.round(e.valuePercentage * (playback.endTime - playback.startTime) + playback.startTime);
        await dispatch(playbackSeekStart({ toTime })).unwrap();
    };

    const handleStopSeek = async () => {
        await dispatch(playbackSeekCancel()).unwrap();
    };

    return (
        <div className="k-card p-2">
            <div className="d-flex gap-2 align-items-center">
                <div className="flex-grow-1 pe-2">
                    <div className="flex-shrink-0 d-flex gap-2 align-items-center">
                        {/* <div className="flex-grow-1">Recording: {playback.name}</div> */}
                        {/* {playback.seek && (
                            <>
                                <Button className="button-small" fillMode="outline" onClick={handleStopSeek}>
                                    Stop
                                </Button>
                                <div>Catching up...</div>
                                <LoadingIndicatorCircular size="1rem" />
                            </>
                        )} */}
                    </div>
                    <div className="px-2">
                        <ProgressSlider
                            progress={progress}
                            snapshots={playback.snapshots}
                            start={playback.startTime}
                            end={playback.endTime}
                            onSeekRequested={handleSeekRequested}
                            seekToTime={playback.seek ? playback.seek.targetTime : undefined}
                        />
                    </div>
                </div>
                <div>
                    <div className="d-flex gap-1 align-items-center">
                        <Button
                            className="button-small"
                            onClick={() => dispatch(changeSpeedPlayback({ speed: playback.speed - 0.25 }))}
                            disabled={playback.speed <= 0.25}
                            svgIcon={DoubleChevronLeftOutline}></Button>
                        <Button
                            className="button-small"
                            onClick={() => dispatch(changeSpeedPlayback({ speed: playback.speed - 0.1 }))}
                            disabled={playback.speed <= 0.1}
                            svgIcon={ChevronLeftOutline}></Button>
                        <div>{playback.speed.toFixed(2)}x</div>
                        <Button
                            className="button-small"
                            onClick={() => dispatch(changeSpeedPlayback({ speed: playback.speed + 0.1 }))}
                            svgIcon={ChevronRightOutline}></Button>
                        <Button
                            className="button-small"
                            onClick={() => dispatch(changeSpeedPlayback({ speed: playback.speed + 0.25 }))}
                            svgIcon={DoubleChevronRightOutline}></Button>
                    </div>
                </div>
                <div className="d-flex gap-1 align-items-center">
                    <Button
                        className="button-small"
                        onClick={() => dispatch(playback.pause ? resumePlayback() : pausePlayback())}
                        svgIcon={playback.pause ? PlayCircleOutline : PauseCircleOutline}
                    />
                    <Button className="button-small" svgIcon={StopCircleOutline} onClick={() => dispatch(stopPlayback())} />
                </div>
            </div>
        </div>
    );
}

function ProgressSlider(props: {
    progress: number;
    start: number;
    snapshots: { time: number; optional: boolean }[];
    end: number;
    seekToTime?: number;
    onSeekRequested: (e: { valuePercentage: number }) => void;
}) {
    const divRef = useRef<HTMLDivElement | null>(null);
    const handleBarClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!divRef.current) {
            return;
        }
        const rect = divRef.current.getBoundingClientRect();
        props.onSeekRequested({ valuePercentage: (event.nativeEvent.clientX - rect.left) / rect.width });
    };

    const snapshots = useMemo(
        () =>
            props.snapshots.map((x) => ({
                left: `${(((x.time - props.start) / (props.end - props.start)) * 100).toPrecision(2)}%`,
                optional: x.optional
            })),
        [props.snapshots, props.start, props.end]
    );

    const ticks = useMemo(() => {
        const spanHours = (props.end - props.start) / 3600;

        if (spanHours > 18) {
            return getTicks({ bigTickInterval: 60 * 60, mediumTickInterval: 30 * 60, labelInterval: 60 * 60 }, props.start, props.end);
        } else if (spanHours > 12) {
            return getTicks({ bigTickInterval: 60 * 60, mediumTickInterval: 30 * 60, labelInterval: 30 * 60 }, props.start, props.end);
        } else if (spanHours > 6) {
            return getTicks(
                { bigTickInterval: 60 * 60, mediumTickInterval: 30 * 60, smallTickInterval: 15 * 60, labelInterval: 30 * 60 },
                props.start,
                props.end
            );
        } else if (spanHours > 2) {
            return getTicks(
                { bigTickInterval: 60 * 60, mediumTickInterval: 30 * 60, smallTickInterval: 15 * 60, labelInterval: 30 * 60 },
                props.start,
                props.end
            );
        }

        return getTicks(
            { bigTickInterval: 60 * 60, mediumTickInterval: 30 * 60, smallTickInterval: 1 * 60, labelInterval: 5 * 60 },
            props.start,
            props.end
        );
    }, [props.start, props.end]);

    return (
        <div className="w-100 h-100 mt-2 position-relative cursor-pointer" onClick={handleBarClick} ref={divRef}>
            <div style={{ height: 2, backgroundColor: 'var(--border-color)', width: '100%' }}></div>

            <Ticks ticks={ticks} />
            {import.meta.env.DEV &&
                snapshots.map((x) => (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: x.left,
                            transform: 'translate(-50%, 0)',
                            width: 2,
                            height: 10,
                            backgroundColor: x.optional ? 'var(--fe-primary)' : 'var(--color-red-400)'
                        }}></div>
                ))}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: (100 - props.progress).toPrecision(3) + '%',
                    height: 2,
                    backgroundColor: 'var(--fe-primary)'
                }}></div>

            <div
                style={{
                    position: 'absolute',
                    top: -3,
                    left: props.progress.toPrecision(3) + '%',
                    height: 8,
                    width: 8,
                    borderRadius: 5,
                    backgroundColor: 'var(--fe-primary)',
                    transform: 'translate(-50%, 0)'
                }}></div>
        </div>
    );
}

type Tick = {
    left: string;
    height: number;
    label?: string;
};

type TickConfig = {
    smallTickInterval?: number;
    mediumTickInterval?: number;
    bigTickInterval?: number;
    labelInterval: number;
};

const Ticks = memo((props: { ticks: Tick[] }) => {
    return (
        <>
            {props.ticks.map((x, i) => (
                <React.Fragment key={i}>
                    <div
                        key={i}
                        style={{
                            position: 'absolute',
                            left: x.left,
                            top: 0,
                            width: 2,
                            height: x.height,
                            transform: 'translate(-1px,0)',
                            background: 'var(--border-color)'
                        }}></div>
                    {x.label && (
                        <div style={{ position: 'absolute', left: x.left, top: 10, transform: 'translate(-50%, 0)', fontSize: '0.65rem' }}>
                            {x.label}
                        </div>
                    )}
                </React.Fragment>
            ))}
        </>
    );
});

function getTicks(config: TickConfig, start: number, end: number) {
    const interval = Math.min(config.smallTickInterval ?? 3600, config.mediumTickInterval ?? 3600, config.bigTickInterval ?? 3600);

    return generateTicks(
        start,
        end,
        interval,
        (x) => {
            if (config.bigTickInterval && x % config.bigTickInterval === 0) {
                return 10;
            }
            if (config.mediumTickInterval && x % config.mediumTickInterval === 0) {
                return 8;
            }
            if (config.smallTickInterval && x % config.smallTickInterval === 0) {
                return 6;
            }

            return 0;
        },
        (x) => (x % config.labelInterval === 0 ? DateTimeFormatter.formatTime(x, 'hmnd') : undefined)
    );
}

function generateTicks(
    start: number,
    end: number,
    interval: number,
    height: number | ((value: number) => number),
    label?: (value: number) => string | undefined
): Tick[] {
    const result = [] as Tick[];
    const range = end - start;
    for (let i = start - (start % interval); i <= end; i += interval) {
        if (i < start || i > end) {
            continue;
        }
        result.push({
            left: (((i - start) / range) * 100).toFixed(2) + '%',
            height: typeof height === 'function' ? height(i) : height,
            label: label?.(i)
        });
    }

    return result;
}
